import { Typography, Box, Grid, MenuItem } from "@mui/material";
import React, { useState } from "react";
import { RHFSelect, RHFTextField } from "../../../../../components/hook-form";
import { vaValues } from "../../../../../@types/vaValues.type";
import { useFormContext } from "react-hook-form";

type Props = {
  fullView: boolean;
};

export const IntermediateVision = ({ fullView }: Props) => {
  const [errorMessages, setErrorMessages] = useState<{
    [key: string]: string | null;
  }>({});

  const handleInputChange = (e: any, fieldName: string) => {
    const value = Number(e.target.value);

    // Define validation for the value
    if (value < 1) {
      setErrorMessages((prev) => ({
        ...prev,
        [fieldName]: "Values less than 1 are not allowed",
      }));
      e.target.value = 1;
    } else if (value > 180) {
      setErrorMessages((prev) => ({
        ...prev,
        [fieldName]: "Values above 180 are not allowed",
      }));
      e.target.value = 180;
    } else {
      setErrorMessages((prev) => ({ ...prev, [fieldName]: null })); // Clear message if valid
    }

    if (e.target.value.length > 3) {
      e.target.value = e.target.value.slice(0, 3); // Enforce a max of 3 digits
    }
  };

  const { watch } = useFormContext();
  return (
    <div>
      <Typography variant="h4" sx={{ mt: 3 }}>
        Intermediate Vision
      </Typography>

      {/* Headings Row */}
      <Grid container spacing={1} sx={{ mt: 1 }}>
        <Grid item xs={1}>
          {/* Empty space for alignment with the first column */}
        </Grid>
        <Grid item xs={1.4}>
          <Typography sx={{ ml: 4 }}>Add</Typography>
        </Grid>
        <Grid item xs={1.4}>
          <Typography sx={{ ml: 4 }}>Sph</Typography>
        </Grid>
        <Grid item xs={1.4}>
          <Typography sx={{ ml: 4 }}>Cyl</Typography>
        </Grid>
        <Grid item xs={1.4}>
          <Typography sx={{ ml: 4 }}>Axis</Typography>
        </Grid>
        <Grid item xs={1.4}>
          <Typography sx={{ ml: 2 }}>Aided VA</Typography>
        </Grid>
        {fullView && (
          <>
            <Grid item xs={1.5}>
              <Typography sx={{ ml: 3 }}>Prism</Typography>
            </Grid>
            <Grid item xs={1.5}>
              <Typography sx={{ ml: 2 }}>Inter PD</Typography>
            </Grid>
          </>
        )}
      </Grid>

      {/* Right Eye Row */}
      <Grid container spacing={1} sx={{ mt: 1 }}>
        <Grid item xs={1}>
          <Typography>Right</Typography>
        </Grid>
        <Grid item xs={1.4}>
          <RHFTextField
            size="small"
            name="autoRefractor.intermediateRightAdd"
            sx={{ width: "90px" }}
          />
        </Grid>
        <Grid item xs={1.4}>
          <RHFTextField
            size="small"
            name="autoRefractor.intermediateRightSPH"
            sx={{ width: "90px" }}
            inputProps={{
              step: 0.25,
              min: -10,
              max: 10,
              onChange: (e: any) => {
                const value = parseFloat(e.target.value).toFixed(2);
                e.target.value = value;
              },
            }}
            InputProps={{
              startAdornment:
                Number(watch(`autoRefractor.intermediateRightSPH`)) > 0
                  ? "+"
                  : null,
            }}
            type="number"
          />
        </Grid>
        <Grid item xs={1.4}>
          <RHFTextField
            size="small"
            name="autoRefractor.intermediateRightCYL"
            sx={{ width: "90px" }}
            inputProps={{
              step: 0.25,
              onChange: (e: any) => {
                let value = parseFloat(e.target.value);
                if (value > 0) {
                  value = -value;
                }
                value = Math.floor(value * 4) / 4;
                e.target.value = value.toFixed(2);
              },
            }}
            type="number"
          />
        </Grid>
        <Grid item xs={1.4}>
          <RHFTextField
            size="small"
            name="autoRefractor.intermediateRightAxis"
            sx={{ width: "90px" }}
            type="number"
            inputProps={{
              step: 1,
              min: 1,
              max: 180,
              onInput: (e: any) => handleInputChange(e, "interRightAxis"),
              onChange: (e: any) => {
                const value = parseFloat(e.target.value).toFixed(2);
                e.target.value = value;
              },
            }}
          />
          <Grid item xs={12}>
            {errorMessages["interRightAxis"] && (
              <Typography color="error" variant="caption">
                {errorMessages["interRightAxis"]}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid item xs={1.4}>
          <RHFSelect
            name="autoRefractor.intermediateRightAidedVA"
            size="small"
            sx={{ width: "90px" }}>
            {vaValues.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </RHFSelect>
        </Grid>
        {fullView && (
          <>
            <Grid item xs={1.5}>
              <RHFTextField
                size="small"
                name="autoRefractor.intermediateRightPrism"
                sx={{ width: "90px" }}
              />
            </Grid>
            <Grid item xs={1.5}>
              <RHFTextField
                size="small"
                name="autoRefractor.intermediateRightInterPD"
                sx={{ width: "90px" }}
                type="number"
                inputProps={{
                  onChange: (e: any) => {
                    const value = parseFloat(e.target.value).toFixed(2);
                    e.target.value = value;
                  },
                }}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Both Eyes Row */}
      <Grid container spacing={1} sx={{ mt: 1 }}>
        {/* Row label (Both) */}
        <Grid item xs={1}>
          <Typography>Both</Typography>
        </Grid>

        {/* Placeholder for Sph, Cyl, Axis */}
        <Grid item xs={1.4} />
        <Grid item xs={1.4} />
        <Grid item xs={1.4} />
        <Grid item xs={1.4} />

        {/* First TextField (Below Aided VA - Fourth Heading) */}
        <Grid item xs={1.4}>
          <RHFSelect
            name="autoRefractor.intermediateBothAidedVA"
            size="small"
            sx={{ width: "90px" }}>
            {vaValues.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </RHFSelect>
        </Grid>

        {fullView && (
          <>
            {/* Second TextField (Below Prism - Fifth Heading) */}
            <Grid item xs={1.5}>
              <RHFTextField
                size="small"
                name="autoRefractor.intermediateBothPrism"
                sx={{ width: "90px" }}
              />
            </Grid>

            {/* Placeholder for intermediate PD */}
            <Grid item xs={2} />
          </>
        )}
      </Grid>

      {/* Left Eye Row */}
      <Grid container spacing={1} sx={{ mt: 1 }}>
        <Grid item xs={1}>
          <Typography>Left</Typography>
        </Grid>
        <Grid item xs={1.4}>
          <RHFTextField
            size="small"
            name="autoRefractor.intermediateLeftAdd"
            sx={{ width: "90px" }}
          />
        </Grid>
        <Grid item xs={1.4}>
          <RHFTextField
            size="small"
            name="autoRefractor.intermediateLeftSPH"
            sx={{ width: "90px" }}
            inputProps={{
              step: 0.25,
              min: -10,
              max: 10,
              onChange: (e: any) => {
                const value = parseFloat(e.target.value).toFixed(2);
                e.target.value = value;
              },
            }}
            InputProps={{
              startAdornment:
                Number(watch(`autoRefractor.intermediateLeftSPH`)) > 0
                  ? "+"
                  : null,
            }}
            type="number"
          />
        </Grid>
        <Grid item xs={1.4}>
          <RHFTextField
            size="small"
            name="autoRefractor.intermediateLeftCYL"
            sx={{ width: "90px" }}
            inputProps={{
              step: 0.25,
              onChange: (e: any) => {
                let value = parseFloat(e.target.value);
                if (value > 0) {
                  value = -value;
                }
                value = Math.floor(value * 4) / 4;
                e.target.value = value.toFixed(2);
              },
            }}
            type="number"
          />
        </Grid>
        <Grid item xs={1.4}>
          <RHFTextField
            size="small"
            name="autoRefractor.intermediateLeftAxis"
            sx={{ width: "90px" }}
            type="number"
            inputProps={{
              step: 1,
              min: 1,
              max: 180,
              onInput: (e: any) => handleInputChange(e, "interLeftAxis"),
              onChange: (e: any) => {
                const value = parseFloat(e.target.value).toFixed(2);
                e.target.value = value;
              },
            }}
          />
          <Grid item xs={12}>
            {errorMessages["interLeftAxis"] && (
              <Typography color="error" variant="caption">
                {errorMessages["interLeftAxis"]}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Grid item xs={1.4}>
          <RHFSelect
            name="autoRefractor.intermediateLeftAidedVA"
            size="small"
            sx={{ width: "90px" }}>
            {vaValues.map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </RHFSelect>
        </Grid>
        {fullView && (
          <>
            <Grid item xs={1.5}>
              <RHFTextField
                size="small"
                name="autoRefractor.intermediateLeftPrism"
                sx={{ width: "90px" }}
              />
            </Grid>
            <Grid item xs={1.5}>
              <RHFTextField
                size="small"
                name="autoRefractor.intermediateLeftInterPD"
                sx={{ width: "90px" }}
                type="number"
                inputProps={{
                  onChange: (e: any) => {
                    const value = parseFloat(e.target.value).toFixed(2);
                    e.target.value = value;
                  },
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );
};
